import React from 'react'

import { DialogBody, DialogFooter, DialogHeader } from '../../components'
import { isForexFuturesCryptoPage } from '../../utils/chart'
import { useForceUpdate } from '../../utils/force-update'
import { useChartLayoutGlobalModel } from './chart-layout-model'
import { ChartExtendedHoursToggle } from './chart/chart-extended-hours-toggle'
import { ChartScaleSelect } from './chart/chart-scale-select'
import { ChartStyleSelect } from './chart/chart-style-select'
import { IndicatorsSelect } from './chart/indicators-select'
import { handleRestoreDefault } from './chart/model-manipulation'
import { OverlaysSelect } from './chart/overlays-select'
import { ChartTabComponentProps, SettingsTab } from './constants'
import { getObjForSubmit, handleUrlUpdateOnSettingsSave, isChartModelValid } from './helpers'
import { useSaveSettings } from './save-settings'
import { SettingsActionFooter } from './settings-action-footer'

export function ChartTab({ onClose, onChangeSettingsTab }: ChartTabComponentProps) {
  const { forceUpdate, forceUpdateKey } = useForceUpdate()
  const { isSaving, saveSettings } = useSaveSettings()
  const { chartLayoutModel, generateNewLayoutModelConfigSnapshot } = useChartLayoutGlobalModel()

  const handleSubmit = React.useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (e.currentTarget.checkValidity() && chartLayoutModel) {
        if (!isChartModelValid(chartLayoutModel)) {
          forceUpdate()
          return
        }

        await saveSettings(getObjForSubmit(chartLayoutModel))
        generateNewLayoutModelConfigSnapshot()
        onClose()

        handleUrlUpdateOnSettingsSave(chartLayoutModel)
      }
    },
    [chartLayoutModel, generateNewLayoutModelConfigSnapshot, forceUpdate, onClose, saveSettings]
  )

  if (!chartLayoutModel) return null

  return (
    <React.Fragment key={forceUpdateKey}>
      <DialogHeader className="landscape-secondary:pr-inset-right" onCloseClick={onClose}>
        Study Settings
      </DialogHeader>
      <DialogBody className="flex-1 p-3 landscape-secondary:mr-inset-right" data-testid="chart-settings-body">
        <form id="chartSettingsForm" onSubmit={handleSubmit} noValidate>
          <div className="space-y-8">
            <div className="grid grid-cols-2 gap-1 xs:grid-cols-[0.6fr_1fr]">
              <ChartStyleSelect />
              <div className="xs:pr-[40%]">
                <ChartScaleSelect />
              </div>
            </div>
            {!isForexFuturesCryptoPage(chartLayoutModel.specificChartFunctionality) && (
              <div>
                <ChartExtendedHoursToggle />
              </div>
            )}
            <div>
              <OverlaysSelect />
            </div>
            <div>
              <IndicatorsSelect />
            </div>
          </div>
        </form>
      </DialogBody>
      <DialogFooter className="!border-0 bg-gray-50 p-1 [--inset-b:0.25rem] landscape-secondary:pr-inset-right landscape-any:pb-inset-bottom dark:bg-gray-700">
        <SettingsActionFooter
          actionButtonFormId="chartSettingsForm"
          isActionPending={isSaving}
          resetConfirmationText="Are you sure you want to restore study settings to their default values? Your custom settings will be lost."
          resetConfirmationTitle="Restore defaults?"
          resetSettingsButtonLabel="Restore defaults"
          settingsTabButtonLabel={
            <>
              <span className="hidden xs:inline">Customize Chart Colors</span>
              <span className="inline xs:hidden">Colors</span>
            </>
          }
          settingsTabButtonIconName="palette"
          tabButtonTestId="chart-colors-tab-button"
          onSettingsTabButtonClick={() => {
            onChangeSettingsTab(SettingsTab.Colors)
          }}
          onSettingsReset={() => {
            handleRestoreDefault({ chartLayoutModel, forceUpdate })
            window.gtag?.('event', 'restore-default', { event_category: 'settings', event_label: 'studies' })
          }}
        />
      </DialogFooter>
    </React.Fragment>
  )
}
